import React, { Component } from "react";
import Helmet from "react-helmet";
import axios from "axios";
import GridLoader from "react-spinners/GridLoader";
import Layout from "../../Layout/layout";
import { apiHost } from "../../api/apiClient";
import "../../styles/maintenance/maintenanceDue.scss";

const DATE_THRESHOLD_KEY = "maintenance_due_date_threshold_days";
const KM_THRESHOLD_KEY = "maintenance_due_km_threshold";

const getValue = (obj, key) => {
  if (!obj) return null;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value) => {
  if (!value || value === "0000-00-00") return "-";
  return String(value).slice(0, 10);
};

const parseDueDate = (value) => {
  if (!value || value === "0000-00-00") return null;
  const parts = String(value).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
};

const formatKm = (value, decimals = 0) => {
  const number = toNumber(value);
  if (number === null) return "-";
  return number.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatDueKm = (value) => {
  const number = toNumber(value);
  if (number === null || number <= 0) return "-";
  return number.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const validStatusFilter = (value) => ["all", "overdue", "upcoming"].includes(value) ? value : "all";

class MaintenanceDue extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(props.location ? props.location.search : "");
    this.state = {
      rows: [],
      isLoading: false,
      errorMessage: "",
      filter: validStatusFilter(query.get("status") || "all"),
      customerFilter: query.get("customerId") || "all",
      search: query.get("search") || "",
      dateThresholdDays: this.getStoredNumber(DATE_THRESHOLD_KEY, 7),
      kmThreshold: this.getStoredNumber(KM_THRESHOLD_KEY, 2000),
      subscribed: false,
      settingsLoading: true,
      settingsSaving: false,
      settingsMessage: "",
      settingsError: "",
    };
  }

  componentDidMount() {
    this.loadMaintenanceSettings();
    this.loadMaintenanceDue();
  }

  getStoredNumber = (key, fallback) => {
    const parsed = Number(localStorage.getItem(key));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  getAuthConfig = () => ({
    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
  });

  loadMaintenanceSettings = async () => {
    try {
      const response = await axios.get(`${apiHost}/api/Assets/MaintenanceSettings`, this.getAuthConfig());
      const settings = response.data || {};
      const dateThresholdDays = Number(getValue(settings, "dateThresholdDays")) || 7;
      const kmThreshold = Number(getValue(settings, "kmThreshold")) || 2000;
      localStorage.setItem(DATE_THRESHOLD_KEY, String(dateThresholdDays));
      localStorage.setItem(KM_THRESHOLD_KEY, String(kmThreshold));
      this.setState({
        dateThresholdDays,
        kmThreshold,
        subscribed: !!getValue(settings, "subscribed"),
        settingsLoading: false,
        settingsError: "",
      });
    } catch (e) {
      this.setState({
        settingsLoading: false,
        settingsError: "Unable to load maintenance alert settings.",
      });
    }
  };

  saveMaintenanceSettings = async (updates = {}) => {
    const nextSettings = {
      dateThresholdDays: updates.dateThresholdDays !== undefined ? updates.dateThresholdDays : this.state.dateThresholdDays,
      kmThreshold: updates.kmThreshold !== undefined ? updates.kmThreshold : this.state.kmThreshold,
      subscribed: updates.subscribed !== undefined ? updates.subscribed : this.state.subscribed,
    };
    this.setState({
      ...nextSettings,
      settingsSaving: true,
      settingsMessage: "",
      settingsError: "",
    });
    try {
      const response = await axios.put(
        `${apiHost}/api/Assets/MaintenanceSettings`,
        nextSettings,
        this.getAuthConfig()
      );
      const saved = response.data || nextSettings;
      const dateThresholdDays = Number(getValue(saved, "dateThresholdDays")) || 7;
      const kmThreshold = Number(getValue(saved, "kmThreshold")) || 2000;
      const subscribed = !!getValue(saved, "subscribed");
      localStorage.setItem(DATE_THRESHOLD_KEY, String(dateThresholdDays));
      localStorage.setItem(KM_THRESHOLD_KEY, String(kmThreshold));
      this.setState({
        dateThresholdDays,
        kmThreshold,
        subscribed,
        settingsSaving: false,
        settingsMessage: subscribed
          ? "Weekly maintenance alerts are enabled."
          : "Weekly maintenance alerts are disabled.",
      });
    } catch (e) {
      this.setState({
        settingsSaving: false,
        settingsError: "Unable to save maintenance alert settings.",
      });
    }
  };

  loadMaintenanceDue = async () => {
    this.setState({ isLoading: true, errorMessage: "" });
    try {
      const response = await axios.get(`${apiHost}/api/Assets/MaintenanceDue`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      const rows = response.data || [];
      const customerFilter = this.state.customerFilter === "all" || rows.some((row) => getValue(row, "customerId") === this.state.customerFilter)
        ? this.state.customerFilter
        : "all";
      this.setState({ rows, customerFilter, isLoading: false }, this.syncUrl);
    } catch (e) {
      const message = e && e.response && e.response.data ? String(e.response.data) : String(e);
      this.setState({ isLoading: false, errorMessage: message });
    }
  };

  syncUrl = () => {
    const params = new URLSearchParams();
    if (this.state.customerFilter !== "all") {
      params.set("customerId", this.state.customerFilter);
      const selectedCustomer = this.getCustomerOptions().find((customer) => customer.id === this.state.customerFilter);
      if (selectedCustomer) params.set("customerName", selectedCustomer.name);
    }
    if (this.state.filter !== "all") params.set("status", this.state.filter);
    if (this.state.search.trim()) params.set("search", this.state.search.trim());

    const query = params.toString();
    const nextUrl = `/maintenance-due${query ? `?${query}` : ""}`;
    const currentUrl = `${this.props.location.pathname}${this.props.location.search}`;
    if (nextUrl !== currentUrl) {
      this.props.history.replace(nextUrl);
    }
  };

  setFilterState = (updates) => {
    this.setState(updates, this.syncUrl);
  };

  getReturnQuery = () => this.props.location && this.props.location.search ? this.props.location.search : "";

  setThreshold = (key, value) => {
    const parsed = Math.max(0, Number(value) || 0);
    localStorage.setItem(key === "dateThresholdDays" ? DATE_THRESHOLD_KEY : KM_THRESHOLD_KEY, String(parsed));
    this.setState({ [key]: parsed });
  };

  today = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  dateStatus = (value) => {
    const date = parseDueDate(value);
    if (!date) return "";
    const days = (date.getTime() - this.today().getTime()) / 86400000;
    if (days <= 0) return "overdue";
    if (days <= this.state.dateThresholdDays) return "upcoming";
    return "";
  };

  kmStatus = (dueKm, odometerKm) => {
    const due = toNumber(dueKm);
    const odometer = toNumber(odometerKm);
    if (due === null || due <= 0 || odometer === null) return "";
    const remaining = due - odometer;
    if (remaining <= 0) return "overdue";
    if (remaining <= this.state.kmThreshold) return "upcoming";
    return "";
  };

  rowStatus = (row) => {
    const statuses = this.rowStatuses(row);
    if (statuses.includes("overdue")) return "overdue";
    if (statuses.includes("upcoming")) return "upcoming";
    return "ok";
  };

  rowStatuses = (row) => (
    [
      this.dateStatus(getValue(row, "registrationDue")),
      this.dateStatus(getValue(row, "insuranceDue")),
      this.dateStatus(getValue(row, "roadworthyDue")),
      this.dateStatus(getValue(row, "serviceDueDate")),
      getValue(row, "isTracked") !== false
        ? this.kmStatus(getValue(row, "serviceDueKm"), getValue(row, "odometerKm"))
        : "",
    ].filter(Boolean)
  );

  rowHasStatus = (row, status) => this.rowStatuses(row).includes(status);

  isSystemAdmin = () => {
    const role = String(localStorage.getItem("role") || "").replace(/\s+/g, "").toLowerCase();
    return role === "systemadmin";
  };

  getCustomerOptions = () => {
    const customersById = {};
    this.state.rows.forEach((row) => {
      const customerId = getValue(row, "customerId") || "";
      if (!customerId) return;
      customersById[customerId] = getValue(row, "customerName") || "Unknown customer";
    });

    return Object.keys(customersById)
      .map((id) => ({ id, name: customersById[id] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  getSelectedCustomer = () => this.getCustomerOptions()
    .find((customer) => customer.id === this.state.customerFilter);

  setCustomerFilter = (customerId) => {
    const selectedCustomer = this.getCustomerOptions().find((customer) => customer.id === customerId);
    if (selectedCustomer) {
      localStorage.setItem("customer", JSON.stringify({
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
      }));
    } else {
      localStorage.removeItem("customer");
    }
    this.setFilterState({ customerFilter: customerId || "all" });
  };

  getFilteredRows = () => {
    const search = this.state.search.trim().toLowerCase();
    return this.getCustomerFilteredRows().filter((row) => {
      if (this.state.filter !== "all" && !this.rowHasStatus(row, this.state.filter)) return false;
      if (!search) return true;
      const haystack = [
        getValue(row, "name"),
        getValue(row, "friendlyName"),
        getValue(row, "customerName"),
        getValue(row, "assetId"),
      ].join(" ").toLowerCase();
      return haystack.indexOf(search) !== -1;
    });
  };

  getCustomerFilteredRows = () => {
    if (this.state.customerFilter === "all") return this.state.rows;
    return this.state.rows.filter((row) => getValue(row, "customerId") === this.state.customerFilter);
  };

  renderFilterButton = (value, label, count) => (
    <button
      type="button"
      className={this.state.filter === value ? "active" : ""}
      onClick={() => this.setFilterState({ filter: value })}
    >
      {label}<span>{count}</span>
    </button>
  );

  renderDueCell = (status, value) => (
    <td className={status || ""}>
      {status && <span className="maintenance-status-mark">{status === "overdue" ? "Overdue" : "Soon"}</span>}
      <span className="maintenance-cell-value">{formatDate(value)}</span>
    </td>
  );

  renderKmCell = (status, value) => (
    <td className={status || ""}>
      {status && <span className="maintenance-status-mark">{status === "overdue" ? "Overdue" : "Soon"}</span>}
      <span className="maintenance-cell-value">{formatDueKm(value)}</span>
    </td>
  );

  renderRows = () => {
    const rows = this.getFilteredRows();
    if (!rows.length) {
      return (
        <tr>
          <td colSpan="11" className="maintenance-empty">No maintenance assets match the selected filters.</td>
        </tr>
      );
    }

    return rows.map((row) => {
      const registrationStatus = this.dateStatus(getValue(row, "registrationDue"));
      const insuranceStatus = this.dateStatus(getValue(row, "insuranceDue"));
      const roadworthyStatus = this.dateStatus(getValue(row, "roadworthyDue"));
      const serviceDateStatus = this.dateStatus(getValue(row, "serviceDueDate"));
      const isTracked = getValue(row, "isTracked") !== false;
      const serviceKmStatus = isTracked
        ? this.kmStatus(getValue(row, "serviceDueKm"), getValue(row, "odometerKm"))
        : "";
      const rowStatus = this.rowStatus(row);

      return (
        <tr
          key={getValue(row, "assetId")}
          className={`maintenance-row-${rowStatus}${!isTracked ? " maintenance-row-non-tracked" : ""}`}
        >
          <td>
            <strong>{getValue(row, "name") || "-"}</strong>
            {getValue(row, "friendlyName") && <span>{getValue(row, "friendlyName")}</span>}
            {!isTracked && <span className="maintenance-asset-type">Non-tracked asset</span>}
          </td>
          <td>
            {this.isSystemAdmin() && getValue(row, "customerId") && getValue(row, "customerName") ? (
              <button
                className="maintenance-customer-link"
                onClick={() => this.setCustomerFilter(getValue(row, "customerId"))}
                title={`Filter maintenance assets by ${getValue(row, "customerName")}`}
                type="button"
              >
                {getValue(row, "customerName")}
              </button>
            ) : (getValue(row, "customerName") || "-")}
          </td>
          {this.renderDueCell(registrationStatus, getValue(row, "registrationDue"))}
          {this.renderDueCell(insuranceStatus, getValue(row, "insuranceDue"))}
          {this.renderDueCell(roadworthyStatus, getValue(row, "roadworthyDue"))}
          {this.renderDueCell(serviceDateStatus, getValue(row, "serviceDueDate"))}
          {isTracked ? this.renderKmCell(serviceKmStatus, getValue(row, "serviceDueKm")) : <td className="maintenance-not-applicable">N/A</td>}
          <td className={!isTracked ? "maintenance-not-applicable" : ""}>{isTracked ? formatKm(getValue(row, "odometerKm"), 1) : "N/A"}</td>
          <td>{getValue(row, "updatedBy") || "-"}</td>
          <td>{formatDateTime(getValue(row, "updatedAt"))}</td>
          <td>
            <button
              type="button"
              className="maintenance-row-action"
              onClick={() => this.props.history.push(`/maintenance-due/${getValue(row, "assetId")}${this.getReturnQuery()}`)}
            >
              View/Edit
            </button>
          </td>
        </tr>
      );
    });
  };

  render() {
    const rows = this.getFilteredRows();
    const customerRows = this.getCustomerFilteredRows();
    const total = customerRows.length;
    const overdue = customerRows.filter((row) => this.rowHasStatus(row, "overdue")).length;
    const upcoming = customerRows.filter((row) => this.rowHasStatus(row, "upcoming")).length;
    const customers = this.getCustomerOptions();
    const selectedCustomer = this.getSelectedCustomer();
    const showCustomerFilter = this.isSystemAdmin() && customers.length > 0;

    return (
      <Layout>
        <Helmet>
          <title>Maintenance Due</title>
        </Helmet>
        <div className="contentWrapper contentWrapperSmoothRelease maintenance-due-page">
          <div className="maintenance-header">
            <div>
              <h2 className="mainHeading">Maintenance Due</h2>
              <p>{this.isSystemAdmin() ? "All tracked and non-tracked assets." : "Assigned tracked assets and customer non-tracked assets."}</p>
              {selectedCustomer && (
                <button
                  className="maintenance-customer-filter-pill"
                  onClick={() => this.setCustomerFilter("all")}
                  title="Remove customer filter"
                  type="button"
                >
                  <i className="fa fa-times"></i>
                  {selectedCustomer.name}
                </button>
              )}
            </div>
            <button type="button" onClick={this.loadMaintenanceDue} disabled={this.state.isLoading}>Refresh</button>
          </div>

          <div className="maintenance-toolbar">
            <label className="maintenance-input-group search">
              <span>Search</span>
              <input
                type="search"
                value={this.state.search}
                onChange={(event) => this.setFilterState({ search: event.target.value })}
                placeholder="Asset, friendly name, customer"
              />
            </label>
            <div className="maintenance-filter-group status">
              <span>Status</span>
              {this.renderFilterButton("all", "All", total)}
              {this.renderFilterButton("overdue", "Overdue", overdue)}
              {this.renderFilterButton("upcoming", "Upcoming", upcoming)}
            </div>
            {showCustomerFilter && (
              <label className="maintenance-input-group customer">
                <span>Customer</span>
                <select
                  value={this.state.customerFilter}
                  onChange={(event) => this.setCustomerFilter(event.target.value)}
                >
                  <option value="all">All customers</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="maintenance-upcoming-settings">
              <div className="maintenance-upcoming-settings-head">
                <h3>Upcoming Threshold Settings</h3>
              </div>
              <div className="maintenance-upcoming-settings-fields">
                <label className="maintenance-input-group compact">
                  <span>Date Window (Days)</span>
                  <input
                    type="number"
                    min="0"
                    value={this.state.dateThresholdDays}
                    onChange={(event) => this.setThreshold("dateThresholdDays", event.target.value)}
                    onBlur={() => this.saveMaintenanceSettings()}
                  />
                </label>
                <label className="maintenance-input-group compact">
                  <span>KM window</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={this.state.kmThreshold}
                    onChange={(event) => this.setThreshold("kmThreshold", event.target.value)}
                    onBlur={() => this.saveMaintenanceSettings()}
                  />
                </label>
              </div>
              <div className={`maintenance-alert-subscription ${this.state.subscribed ? "subscribed" : ""}`}>
                <span className="maintenance-alert-icon">
                  <i className={`fa ${this.state.subscribed ? "fa-bell" : "fa-bell-o"}`}></i>
                </span>
                <div>
                  <strong>{this.state.subscribed ? "Subscribed to alerts" : "Subscribe to alerts"}</strong>
                  <p>Receive a weekly maintenance email when any item becomes due or overdue.</p>
                  {this.state.settingsMessage && <small className="success">{this.state.settingsMessage}</small>}
                  {this.state.settingsError && <small className="error">{this.state.settingsError}</small>}
                </div>
                <button
                  type="button"
                  disabled={this.state.settingsLoading || this.state.settingsSaving}
                  onClick={() => this.saveMaintenanceSettings({ subscribed: !this.state.subscribed })}
                >
                  {this.state.settingsSaving
                    ? "Saving..."
                    : this.state.subscribed
                      ? "Unsubscribe"
                      : "Subscribe"}
                </button>
              </div>
            </div>
          </div>

          {this.state.errorMessage && <div className="maintenance-error">{this.state.errorMessage}</div>}

          <div className="maintenance-table-panel">
            <div className="maintenance-table-head">
              <strong>{rows.length.toLocaleString()} assets</strong>
              <span>Date window: {this.state.dateThresholdDays} days. KM window: {this.state.kmThreshold.toLocaleString()} km.</span>
            </div>
            {this.state.isLoading ? (
              <div className="maintenance-loading">
                <GridLoader size={8} color={"#222222"} loading />
                <span>Loading maintenance due</span>
              </div>
            ) : (
              <div className="maintenance-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Customer</th>
                      <th>Registration</th>
                      <th>Insurance</th>
                      <th>Roadworthy</th>
                      <th>Service Date</th>
                      <th>Service KM</th>
                      <th>Odometer</th>
                      <th>Updated By</th>
                      <th>Updated At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>{this.renderRows()}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }
}

export default MaintenanceDue;
